<template>
  <v-dialog
    v-model="dialog"
    persistent
    transition="dialog-bottom-transition"
    max-width="1000"
    v-if="isDataLoaded"
  >
    <!--begin::Modal dialog-->
    <div class="bg-white poppins">
      <!--begin::Modal header-->
      <div class="modal-header py-4 align-center">
        <h4 class="mb-0 font-weight-bolder">{{ headerText }}</h4>
        <!--begin::Close-->
        <div
          class="btn btn-sm btn-icon btn-active-light-info"
          @click="toggleModal"
        >
          <span class="svg-icon">
            <v-icon size="20">mdi-close</v-icon>
          </span>
        </div>
        <!--end::Close-->
      </div>
      <!--end::Modal header-->
      <!--begin::Modal body-->
      <div class="modal-body d-flex flex-column scroll-y mx-5 py-7">
        <form class="row ma-0">
          <div class="col-12 col-sm-6 h-75px">
            <v-text-field
              v-model="formData.name"
              :label="$t('name')"
              dense
              outlined
              clearable
              :error-messages="nameErrors"
              @blur="$v.formData.name.$touch()"
            />
          </div>

          <div class="col-12 col-sm-6 col-md-3 h-75px">
            <v-select
              v-model="formData.apply_type_id"
              :label="$t('apply_type')"
              :items="serverData.apply_types"
              item-text="text"
              item-value="index"
              dense
              outlined
              hide-no-data
              hide-selected
            />
          </div>

          <div class="col-12 col-sm-6 col-md-3 h-75px">
            <v-text-field
              v-model="formData.priority"
              :label="$t('priority')"
              type="number"
              dense
              outlined
              clearable
              :error-messages="priorityErrors"
              @blur="$v.formData.priority.$touch()"
              @change="() => validateMinValue('priority', 0)"
            />
          </div>

          <div
            class="col-12 col-sm-6 col-md-4"
            v-if="!$store.getters.isClient && formData.apply_type_id === 2"
          >
            <v-combobox
              v-model="formData.clients"
              :label="$t('customer')"
              :items="customers"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable
              multiple
              small-chips
              deletable-chips
              :error-messages="customerIdErrors"
              @blur="$v.formData.clients.$touch()"
            >
              <template v-slot:prepend-item>
                <v-list-item ripple @click="toggle">
                  <v-list-item-action>
                    <v-icon>
                      {{ icon }}
                    </v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title class="font-size-sm">
                      Select All
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="mt-2"></v-divider>
              </template>
            </v-combobox>
          </div>

          <div
            class="col-12 col-sm-6 h-75px"
            :class="{
              'col-md-4':
                !$store.getters.isClient && formData.apply_type_id === 2,
            }"
          >
            <v-select
              v-model="formData.model_type_id"
              :label="$t('model_type')"
              :items="serverData.model_types"
              item-text="text"
              item-value="index"
              dense
              outlined
              hide-no-data
              hide-selected
              @change="handleModelChange"
            />
          </div>

          <div
            class="col-12 col-sm-6 h-75px"
            :class="{
              'col-md-4':
                !$store.getters.isClient && formData.apply_type_id === 2,
            }"
          >
            <v-select
              v-model="formData.process_sequence"
              :label="$t('process_sequence')"
              :items="serverData.process_sequences"
              item-text="text"
              item-value="index"
              dense
              outlined
            />
          </div>

          <div class="col-12 h-75px">
            <v-text-field
              v-model="formData.description"
              :label="$t('description')"
              dense
              outlined
              clearable
            />
          </div>

          <div class="col-12 col-sm-6 col-md-4 h-75px">
            <v-autocomplete
              v-model="formData.status"
              :label="$t('status')"
              :items="statusValues"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable
              hide-no-data
              hide-selected
              :error-messages="statusErrors"
              @blur="$v.formData.status.$touch()"
            />
          </div>

          <div class="col-12 col-sm-6 col-md-4 h-75px">
            <v-autocomplete
              v-model="formData.type_id"
              :label="$t('rule_type')"
              :items="ruleTypes"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable
              hide-no-data
              hide-selected
              :error-messages="typeErrors"
              @blur="$v.formData.type_id.$touch()"
            />
          </div>

          <div
            class="col-12 col-sm-6 col-md-4 h-75px"
            v-if="formData.type_id != '1' && formData.type_id != 3"
          >
            <v-autocomplete
              v-model="formData.actions"
              :label="$t('actions')"
              :items="actions"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable
              hide-no-data
              hide-selected
              :error-messages="actionErrors"
              @blur="$v.formData.actions.$touch()"
            />
          </div>

          <div class="col-12 col-sm-6 col-md-4 h-75px">
            <v-checkbox
              class="mt-0"
              v-model="formData.skip_rule_after_pending_audit"
              :label="'Skip rule while auditing'"
              :true-value="1"
              :false-value="0"
              :indeterminate="formData.skip_rule_after_pending_audit === null"
              clearable
            />
          </div>
        </form>

        <div
          v-if="formData.type_id === 2 && formData.type_id != 3"
          class="mt-4 pa-0 w-100"
        >
          <RuleBuilder
            :query="query"
            :options="operands"
            :operators="operators"
            :userId="userId"
            ref="ruleBuilder"
          />
        </div>
        <div
          v-if="
            formData.actions === 3 &&
            formData.type_id != '1' &&
            formData.type_id != 3
          "
          class="mt-4 pa-0 w-100"
        >
          <ActionBuilder
            :query="formData.attributes"
            :options="
              formData.model_type_id === 1
                ? serverData.attributes
                : serverData.return_attributes
            "
            :operators="serverData"
            :userId="userId"
            ref="actionBuilder"
          />
        </div>

        <!--begin::Actions-->
        <div class="my-4 h-75px d-flex flex-grow-1 align-end justify-center">
          <button
            type="reset"
            class="btn btn-light mr-3 px-5 py-3 ls1"
            @click="resetFormData"
          >
            Clear
          </button>
          <button
            type="submit"
            class="btn btn-info px-5 py-3 ls1"
            @click="submitForm"
          >
            {{ submitButtonText }}
          </button>
        </div>
        <!--end::Actions-->
      </div>
      <!--end::Modal body-->
    </div>
    <!--end::Modal dialog-->
  </v-dialog>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";
import swalEdited from "@/core/services/AlertServices/SwalEdited";
import swalAdded from "@/core/services/AlertServices/SwalAdded";
import RuleBuilder from "@/own/components/orderManagement/rules/ruleBuilder/RuleBuilder";
import ActionBuilder from "@/own/components/orderManagement/rules/actionBuilder/ActionBuilder";
import Swal from "sweetalert2";
import fieldValueValidation from "@/own/mixins/fieldValueValidation.js";

export default {
  name: "ActionsDialog",
  props: ["refresher", "pageLoader"],
  mixins: [validationMixin, fieldValueValidation],
  components: { RuleBuilder, ActionBuilder },
  validations() {
    return {
      formData: {
        clients: {
          required: requiredIf(function () {
            return this.formData.apply_type_id != "1";
          }),
        },
        name: { required },
        status: { required },
        type_id: { required },
        priority: { required },
        actions: {
          required: requiredIf(function () {
            return this.formData.type_id != "1" && this.formData.type_id != 3;
          }),
        },
      },
    };
  },
  data: () => ({
    dialog: false,
    serverData: null,
    formData: {
      clients: null,
      process_sequence: 1,
      name: null,
      priority: null,
      status: null,
      type_id: 2,
      description: null,
      actions: 3,
      attributes: [],
      apply_type_id: 2,
      model_type_id: 1,
      skip_rule_after_pending_audit: 0,
    },
    query: {
      condition: "and",
      rules: [],
    },
  }),
  methods: {
    toggleModal() {
      if (this.dialog) {
        this.resetFormData();
        this.serverData = null;
      } else {
        this.loadDataFromServer();
      }
      this.dialog = !this.dialog;
    },
    resetFormData() {
      this.formData = {
        clients: null,
        process_sequence: 1,
        name: null,
        priority: null,
        status: null,
        type_id: 2,
        description: null,
        actions: 3,
        attributes: [],
        apply_type_id: 2,
        model_type_id: 1,
        skip_rule_after_pending_audit: 0,
      };
      this.query = {
        condition: "and",
        rules: [],
      };
    },
    loadDataFromServer() {
      // this.$store.dispatch(GET_DATA);
      this.pageLoader(true);
      if (this.actionType) {
        let sendData = { id: this.$store.getters.getItemForAction.id };
        ApiService.post(
          "/order_management/order_management_rules/edit",
          sendData
        )
          .then(({ data }) => {
            if (data.rule.condition)
              this.query = JSON.parse(data.rule.condition);
            delete data.rule.condition;
            this.serverData = data;
            this.formData = data.rule;
            this.formData.actions = data.rule.actions[0]; //spartanskiyy
            this.formData.clients = data.clients.filter((item) =>
              data.rule.clients.includes(item.index)
            );
            // console.log(" this.query", this.query);
            // console.log(" this.formData", this.formData);
            this.pageLoader(false);
          })
          .catch(() => {
            this.dialog = false;
            this.pageLoader(false);
          });
      } else {
        ApiService.post("/order_management/order_management_rules/create")
          .then(({ data }) => {
            this.serverData = data;
            this.pageLoader(false);
          })
          .catch(() => {
            this.dialog = false;
            this.pageLoader(false);
          });
      }
    },
    handleFormValidation(fieldName) {
      const errors = [];
      if (!this.$v.formData[fieldName].$dirty) return errors;
      if ("required" in this.$v.formData[fieldName]) {
        !this.$v.formData[fieldName].required &&
          errors.push(this.$t("validation_this_field_is_required"));
      }
      return errors;
    },
    validateActionBuilderData() {
      let isNotValid = false;
      this.formData.attributes.forEach((attr) => {
        if (
          attr.value === undefined ||
          attr.value === null ||
          attr.value === ""
        ) {
          isNotValid = true;
        }
      });
      return isNotValid;
    },
    submitForm() {
      this.$v.$touch();
      // console.log("sendData", this.query);
      if (this.formData.type_id === 2) {
        if (!this.query.rules.length) {
          Swal.fire({
            title: "Warning",
            text: "Please, add at least one rule!",
            icon: "warning",
          });
          return;
        }

        if (this.$store.getters.getOMSRULESNotValid) {
          // console.log('oms rules not valid', this.$store.getters.getOMSRULESNotValid);
          return;
        } //else console.log("noooo");
        if (this.formData.actions === 3) {
          if (!this.formData.attributes.length) {
            Swal.fire({
              title: "Warning",
              text: "Please, add at least one action!",
              icon: "warning",
            });
            return;
          }
          if (this.validateActionBuilderData()) {
            // console.log('actions validation error');
            return;
          }
        } else {
          this.formData.attributes = [];
        }
      }

      if (this.$v.$invalid) {
        // console.log("form is invalid", this.$v);
        return;
      }
      this.pageLoader(true);
      let sendData = {
        ...this.formData,

        actions: [this.formData.actions], //spartanskiyy
      };
      if (this.formData.apply_type_id != "1") {
        sendData.clients = this.formData.clients.map((item) => item.index);
      }
      if (this.formData.type_id === 2) {
        sendData.condition = JSON.stringify(this.query);
      } else {
        sendData.actions = [];
        sendData.attributes = [];
      }
      if (this.actionType) {
        sendData.id = this.$store.getters.getItemForAction.id;
      }
      // console.log("sendData", sendData);
      let actionUrl = "store";
      if (this.actionType) actionUrl = "update";
      ApiService.post(
        `/order_management/order_management_rules/${actionUrl}`,
        sendData
      )
        .then(() => {
          if (this.actionType) swalEdited();
          else swalAdded();
          this.toggleModal();
          this.refresher();
        })
        .catch(() => {
          this.pageLoader(false);
        });
    },
    toggle() {
      if (this.selectedAll) {
        this.$set(this.formData, "clients", null);
      } else {
        let arr = [...this.customers];
        this.$set(this.formData, "clients", arr);
      }
    },
    handleModelChange() {
      this.query = {
        condition: "and",
        rules: [],
      };
      this.formData.attributes = [];
      this.$nextTick(() => {
        this.$refs.actionBuilder.addDefault();
        this.$refs.ruleBuilder.addDefault();
      });
    },
  },
  computed: {
    selectedAll() {
      if (Array.isArray(this.formData.clients)) {
        return this.customers.length === this.formData.clients.length;
      } else return false;
    },
    selectedSome() {
      if (Array.isArray(this.formData.clients)) {
        return this.formData.clients.length > 0;
      } else return false;
    },
    icon() {
      if (this.selectedAll) return "mdi-checkbox-marked";
      if (this.selectedSome) return "mdi-minus-box-outline";
      return "mdi-checkbox-blank-outline";
    },
    // if true, we are editing item, false we are adding
    actionType: function () {
      return !!this.$store.getters.getItemForAction;
    },
    submitButtonText: function () {
      if (this.actionType) return "Update";
      else return "Submit";
    },
    headerText: function () {
      if (this.actionType) return "Edit Item";
      else return "Add New Item";
    },
    userId: function () {
      return this.$store.getters.isClient
        ? this.$store.getters.currentUser.data.id
        : this.formData.clients;
    },
    customers: function () {
      return this.serverData.clients;
    },
    statusValues: function () {
      return this.serverData.statuses;
    },
    ruleTypes: function () {
      return this.serverData.types;
    },
    actions: function () {
      return this.serverData.actions;
    },
    operands: function () {
      return this.formData.model_type_id === 1
        ? this.serverData.operands
        : this.serverData.returnOperands;
    },
    operators: function () {
      return this.serverData.operators;
    },
    customerIdErrors: function () {
      return this.handleFormValidation("clients");
    },
    nameErrors: function () {
      return this.handleFormValidation("name");
    },
    priorityErrors: function () {
      return this.handleFormValidation("priority");
    },
    statusErrors: function () {
      return this.handleFormValidation("status");
    },
    typeErrors: function () {
      return this.handleFormValidation("type_id");
    },
    actionErrors: function () {
      return this.handleFormValidation("actions");
    },
    isDataLoaded() {
      return !!this.serverData;
    },
  },
};
</script>
